import React from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  Text,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { CampaignSelector } from '../CampaignSelector';
import { GroupNameInput } from '../GroupNameInput';
import { Campaign } from '../CampaignList';

type CreateGroupDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  onGroupNameChange: (name: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCampaigns: Set<string>;
  onToggleCampaign: (campaignId: string) => void;
  allCampaigns: Campaign[];
  onCreate: () => void;
};

export const CreateGroupDrawer: React.FC<CreateGroupDrawerProps> = ({
  isOpen,
  onClose,
  groupName,
  onGroupNameChange,
  searchQuery,
  onSearchChange,
  selectedCampaigns,
  onToggleCampaign,
  allCampaigns,
  onCreate,
}) => {
  const isCampaignChecked = (campaignId: string) => {
    return selectedCampaigns.has(campaignId);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="32px"
            color="content.title">
            Create New Group
          </Text>
        </DrawerHeader>

        <DrawerBody p={6}>
          <VStack align="stretch" spacing={6}>
            {/* Group Name Input */}
            <GroupNameInput
              label="Group Name"
              value={groupName}
              onChange={onGroupNameChange}
              placeholder="Name your group"
            />

            {/* Campaigns List */}
            <Box>
              <Text
                fontFamily="marketingPage"
                fontSize="16px"
                fontWeight={500}
                color="content.title"
                mb={3}>
                Campaigns
              </Text>
              <CampaignSelector
                campaigns={allCampaigns}
                selectedCampaignIds={selectedCampaigns}
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
                onToggleCampaign={onToggleCampaign}
                isChecked={isCampaignChecked}
              />
            </Box>
          </VStack>
        </DrawerBody>

        {/* Footer Buttons */}
        <Box p={6} display="flex" gap={4}>
          <Button
            bg="surface.sidebarMuted"
            color="content.body"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-create-group"
            fontFamily="marketingPage"
            onClick={onClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="create-new-group"
            fontFamily="marketingPage"
            onClick={onCreate}
            flex={1}
            isDisabled={!groupName.trim() || selectedCampaigns.size === 0}
            _hover={{ bg: 'green.600' }}>
            Create New Group
          </Button>
        </Box>
      </DrawerContainer>
    </Drawer>
  );
};
